/**
 * ============================================================
 * 🔊 AudioService - Serviço Central de Áudio
 * ============================================================
 *
 * REGRA DE OURO: AudioContext NUNCA é criado automaticamente.
 * Só é criado dentro de forceActivate() que DEVE ser chamado
 * em resposta a um gesto do usuário (click/touch).
 *
 * Antes da ativação, playAlert() usa APENAS HTML5 Audio (new Audio()).
 * Isso elimina 100% dos warnings "AudioContext was not allowed to start".
 *
 * ESTRATÉGIA:
 * 1. Antes do gesto: HTML5 Audio com data URI (funciona em background)
 * 2. Após forceActivate(): AudioContext com osciladores (melhor qualidade)
 * 3. Fallback: se AudioContext falhar, volta pro HTML5 Audio
 *
 * ✅ FIX DEFINITIVO 2026-04-30: Zero AudioContext sem gesto do usuário
 */

export type AlertSoundType = 'ifood' | 'classic' | 'urgent' | 'soft';

// ============================================================
// 🔒 ESTADO INTERNO DO SERVIÇO
// ============================================================
let audioContext: AudioContext | null = null;
let isActivated = false;

// ============================================================
// 🎵 BEEP HTML5 AUDIO (funciona SEM gesto, SEM AudioContext)
// WAV mínimo: 3 bips ascendentes em base64
// ============================================================
const createBeepDataURI = (freq1: number, freq2: number, freq3: number): string => {
  const sampleRate = 22050;
  const duration = 0.15;
  const gap = 0.08;
  const totalSamples = Math.floor(sampleRate * (duration * 3 + gap * 2));

  const buffer = new ArrayBuffer(44 + totalSamples * 2);
  const view = new DataView(buffer);

  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + totalSamples * 2, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, totalSamples * 2, true);

  const freqs = [freq1, freq2, freq3];
  let sampleIdx = 0;
  for (let bip = 0; bip < 3; bip++) {
    const bipSamples = Math.floor(sampleRate * duration);
    for (let i = 0; i < bipSamples; i++) {
      const t = i / sampleRate;
      const envelope = Math.min(t * 20, 1) * Math.min((duration - t) * 20, 1);
      const sample = Math.sin(2 * Math.PI * freqs[bip] * t) * envelope * 0.8 * 32767;
      view.setInt16(44 + sampleIdx * 2, Math.round(sample), true);
      sampleIdx++;
    }
    if (bip < 2) {
      const gapSamples = Math.floor(sampleRate * gap);
      for (let i = 0; i < gapSamples; i++) {
        view.setInt16(44 + sampleIdx * 2, 0, true);
        sampleIdx++;
      }
    }
  }

  const uint8 = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
  return 'data:audio/wav;base64,' + btoa(binary);
};

// ============================================================
// 🎵 CACHE DE DATA URIs POR TIPO DE SOM
// ============================================================
const BEEP_CACHE: Record<string, string> = {};

const getBeepURI = (type: AlertSoundType): string => {
  if (BEEP_CACHE[type]) return BEEP_CACHE[type];

  switch (type) {
    case 'ifood':
      BEEP_CACHE[type] = createBeepDataURI(880, 1100, 1320);
      break;
    case 'classic':
      BEEP_CACHE[type] = createBeepDataURI(800, 800, 800);
      break;
    case 'urgent':
      BEEP_CACHE[type] = createBeepDataURI(1100, 1100, 1100);
      break;
    case 'soft':
      BEEP_CACHE[type] = createBeepDataURI(660, 660, 660);
      break;
    default:
      BEEP_CACHE[type] = createBeepDataURI(880, 1100, 1320);
  }
  return BEEP_CACHE[type];
};

// ============================================================
// 🔊 HTML5 AUDIO PLAYER (NÃO requer gesto, NÃO usa AudioContext)
// ============================================================
const playHTML5Audio = (type: AlertSoundType): Promise<boolean> => {
  return new Promise((resolve) => {
    try {
      const audio = new Audio(getBeepURI(type));
      audio.volume = 1.0;
      audio.onended = () => resolve(true);
      audio.onerror = () => resolve(false);
      audio.play().catch(() => resolve(false));
    } catch {
      resolve(false);
    }
  });
};

// ============================================================
// 🎹 TOCAR TOM VIA AUDIOCONTEXT (só após ativação)
// ============================================================
const playTone = (
  ctx: AudioContext,
  startTime: number,
  frequency: number,
  duration: number,
  volume: number = 0.8,
  type: OscillatorType = 'sine'
): void => {
  try {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = type;

    const time = ctx.currentTime + startTime;
    gainNode.gain.setValueAtTime(volume, time);
    gainNode.gain.exponentialRampToValueAtTime(0.001, time + duration);

    oscillator.start(time);
    oscillator.stop(time + duration + 0.05);
  } catch (e) {
    console.warn('[AudioService] Erro ao tocar tom:', e);
  }
};

// ============================================================
// 🎵 PADRÕES DE SOM VIA AUDIOCONTEXT
// ============================================================
const SOUND_PATTERNS: Record<AlertSoundType, (ctx: AudioContext) => number> = {
  ifood: (ctx) => {
    playTone(ctx, 0.0, 880, 0.12, 0.9, 'sine');
    playTone(ctx, 0.18, 1100, 0.12, 0.9, 'sine');
    playTone(ctx, 0.36, 1320, 0.20, 1.0, 'sine');
    return 800;
  },
  classic: (ctx) => {
    playTone(ctx, 0.0, 800, 0.15, 0.8, 'sine');
    playTone(ctx, 0.25, 800, 0.15, 0.8, 'sine');
    return 600;
  },
  urgent: (ctx) => {
    for (let i = 0; i < 5; i++) {
      playTone(ctx, i * 0.18, 1100, 0.12, 1.0, 'square');
    }
    return 1200;
  },
  soft: (ctx) => {
    playTone(ctx, 0.0, 660, 0.25, 0.5, 'sine');
    return 400;
  },
};

// ============================================================
// 🎯 INTERFACE PÚBLICA
// ============================================================
export const audioService = {

  /**
   * 🔓 Forçar ativação do AudioContext
   * DEVE ser chamado APENAS dentro de um handler de clique/touch do usuário.
   * É o ÚNICO lugar onde new AudioContext() é permitido.
   */
  forceActivate: async (): Promise<boolean> => {
    try {
      // Já ativado? Retorna true sem fazer nada
      if (isActivated && audioContext && audioContext.state === 'running') {
        return true;
      }

      const AC = window.AudioContext || (window as any).webkitAudioContext;
      if (!AC) {
        console.warn('[AudioService] AudioContext não suportado');
        isActivated = true; // Marca como ativado para usar HTML5 fallback
        return true;
      }

      // Criar AudioContext APENAS AQUI (dentro de gesto do usuário)
      if (!audioContext) {
        audioContext = new AC();
      }

      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      // Tocar silêncio para desbloquear no iOS
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      gain.gain.setValueAtTime(0.001, audioContext.currentTime);
      osc.connect(gain);
      gain.connect(audioContext.destination);
      osc.start(audioContext.currentTime);
      osc.stop(audioContext.currentTime + 0.1);

      isActivated = true;
      console.log('🔊 [AudioService] Ativado com sucesso! State:', audioContext.state);
      return true;
    } catch (e) {
      console.warn('[AudioService] forceActivate falhou:', e);
      // Mesmo com falha, marca como ativado para usar HTML5 fallback
      isActivated = true;
      return true;
    }
  },

  /**
   * 🔔 Tocar alerta sonoro
   *
   * LÓGICA:
   * - Se AudioContext está running → usa osciladores (melhor qualidade)
   * - Se AudioContext não está pronto → usa HTML5 Audio (new Audio())
   * - NUNCA cria AudioContext aqui. NUNCA chama resume() aqui.
   */
  playAlert: async (soundType: AlertSoundType = 'ifood'): Promise<boolean> => {
    // Tentar AudioContext APENAS se já foi ativado por gesto
    if (audioContext && audioContext.state === 'running') {
      return new Promise((resolve) => {
        try {
          const pattern = SOUND_PATTERNS[soundType] || SOUND_PATTERNS.ifood;
          const duration = pattern(audioContext!);
          console.log(`🔊 [AudioService] Tocando: ${soundType} via AudioContext (${duration}ms)`);
          setTimeout(() => resolve(true), duration);
        } catch (e) {
          console.warn('[AudioService] AudioContext falhou, usando HTML5 fallback:', e);
          playHTML5Audio(soundType).then(resolve);
        }
      });
    }

    // AudioContext não disponível → HTML5 Audio (funciona sem gesto em muitos browsers)
    console.log(`🔊 [AudioService] Tocando: ${soundType} via HTML5 Audio (sem AudioContext)`);
    return playHTML5Audio(soundType);
  },

  /**
   * 🗣️ Text-to-Speech — DESABILITADO
   */
  speak: (_text: string, _lang: string = 'pt-BR'): void => {
    // TTS desabilitado globalmente
  },

  /**
   * 🔇 Parar TTS
   */
  stopSpeech: (): void => {
    if ('speechSynthesis' in window) {
      try { window.speechSynthesis.cancel(); } catch (_) {}
    }
  },

  /**
   * 📳 Vibrar
   */
  vibrate: (pattern: number | number[] = [200, 100, 200]): void => {
    if (!('vibrate' in navigator)) return;
    try {
      navigator.vibrate(pattern);
    } catch (e) {
      console.warn('[AudioService] Vibração falhou:', e);
    }
  },

  /**
   * 📳 Parar vibração
   */
  stopVibration: (): void => {
    if ('vibrate' in navigator) {
      try { navigator.vibrate(0); } catch (_) {}
    }
  },

  /**
   * 📊 Status do serviço
   */
  getStatus: () => ({
    isActivated,
    audioContextState: audioContext?.state ?? 'não criado',
    ttsSupported: 'speechSynthesis' in window,
    vibrationSupported: 'vibrate' in navigator,
  }),
};

export default audioService;
